import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { CurrentLanguage } from 'projects/Autofacets-Core/src/app/common-services/current-language.service';
import { TemplateScope, TemplateType } from '../../../../../../Autofacets-Core/src/app/utils/enums';
import { ActionTypeModel } from '../../../business-entities/action-type-model';
import { ModuleModel } from '../../../business-entities/module-model';
import { SelectListItemViewModel } from '../../../business-entities/select-list-item-view-model';
import { TemplateDescriptionModel } from '../../../business-entities/template-description-model';
import { TemplateModel } from '../../../business-entities/template-model';
import { TemplateUsageModel } from '../../../business-entities/template-usage-model';
import { UserTypeModel } from '../../../business-entities/user-type-model';
import { TemplateBusinessService } from '../../../business-services/template-service';
import Quill from 'quill';



@Component({
  selector: 'app-edit-template',
  templateUrl: './edit-template.component.html',
  styleUrls: ['./edit-template.component.css']
})
export class EditTemplateComponent implements OnInit {

  //#region Declaration

  @Input() templateId: number = 0;
  templateInfo: TemplateModel;

  moduleList: ModuleModel[];
  actionTypeList: ActionTypeModel[];
  templateUsageList: TemplateUsageModel[];
  dealerList: SelectListItemViewModel[];
  userTypeList: UserTypeModel[];
  templateTypeList: SelectListItemViewModel[];
  channelType = TemplateType;

  editTemplateForm: FormGroup;
  templateFieldList = [];
  templateField: string;
  templateScopeList = Object.keys(TemplateScope)
    .filter(key => !isNaN(Number(key)))
    .map(key => ({
      id: Number(key),
      name: TemplateScope[key]
    }));
  languageList: any[];
  selectedLanguage: any; // language object
  templateDescriptions: TemplateDescriptionModel[] = [];
  quill: Quill;
  options = {
    toolbar: {
      container: [
        [{'undo':'Undo'}, {'redo': 'Redo'}],
        [{'direction': 'rtl'}, 'blockquote'],
        [{ 'header': 1 }, { 'header': 2 }, { 'header': 3 }, { 'header': 4 }],
        ['bold', 'italic', 'strike'],
        [{'script': 'super'}, {'script': 'sub'}],
        ['link'],
        [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
        [{'list': 'ordered'}, {'list': 'bullet'}],
        ['clean']
      ],
      handlers: {
        redo() {
          this.quill.history.redo();
        },
        undo() {
          this.quill.history.undo();
        }
      }
    },
    history: {delay: 500, maxStack: 500, userOnly: true}
  };
 quillEditors: Map<number, Quill> = new Map();
  private contentLoadedMap: Map<number, boolean> = new Map();
 
  //#endregion

  //#region Constructor & OnInit

  constructor(
    private editTemplateService: TemplateBusinessService,
    private spinner: NgxSpinnerService,
    private toaster: ToastrService,
    private translate: TranslateService,
    private formBuilder: FormBuilder,
    private currentLanguageService: CurrentLanguage,
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.languageList = this.currentLanguageService.LanguageList;
    this.GetTemplateDetails(this.templateId);
    this.editTemplateForm = this.formBuilder.group({
      dealerId: [{ value: '', disabled: true }, Validators.required],
      templateType: [{ value: '', disabled: true }, Validators.required],
      applicationId: [{ value: '', disabled: true }, Validators.required],
      templateUsageId: [{ value: '', disabled: true }, Validators.required],
      templateScope: [null],
      cc: [''],
      bcc: [''],
      actionTypeCode: [{ value: '', disabled: true }, Validators.required],
      userTypeId: [{ value: '', disabled: true }, Validators.required],
      isActive: [''],
      template: new FormArray([])
    });
  }

  onEditorCreated(quill: Quill, index: number) {
    this.quill = quill;
    this.quillEditors.set(index, quill);
    
    const currentBody = this.templateDescriptions[index]?.body;
    if (currentBody && !this.contentLoadedMap.get(index)) {
      this.contentLoadedMap.set(index, true);
      setTimeout(() => {
        quill.root.innerHTML = currentBody;
      }, 0);
    }
  }
  //#endregion

  //#region Page Methods

  get cpf() {
    return this.editTemplateForm.controls;
  }

  get t() { return this.cpf.template as FormArray; }

  getLanguageForm() {
    const number = this.currentLanguageService.LanguageList.length;
    if (this.t.length < number) {
      for (let i = this.t.length; i < number; i++) {
        const langId = this.currentLanguageService.LanguageList[i].id;
        this.templateDescriptions[i].languageId = langId;
        this.t.push(this.formBuilder.group({
          languageId: [langId],
          name: ['', Validators.required],
          subject: [''],
          body: ['', Validators.required],
        }));
      }
    } else {
      for (let i = this.t.length; i >= number; i--) {
        this.t.removeAt(i);
      }
    }
  }

  setLists() {
    this.moduleList = this.templateInfo.moduleList;
    this.templateTypeList = this.templateInfo.templateTypeList;
    this.templateUsageList = this.templateInfo.templateUsageList;
    this.dealerList = this.editTemplateService.dealerList;
    this.userTypeList = this.templateInfo.userTypeList;
    this.actionTypeList = this.templateInfo.actionTypeList;

    this.templateDescriptions = this.templateInfo.templateDescriptions;
  }

  onModuleChange(){
    const fields = this.moduleList.find(x => x.id == this.templateInfo.applicationId)
    if (fields?.templateField) {
      this.templateField = fields.templateField;
      this.templateFieldList = this.templateField.split(',');
      this.templateFieldList = this.templateFieldList.slice().sort((a, b) => {
        return (a < b ? -1 : 1);
      });
    }
    else {
      this.templateFieldList = [];
    }
  }

  disabled() {
    let isDisabled: boolean = true;
    if (this.templateInfo?.templateType == this.channelType.Sms) {
      this.t.controls.forEach(element => {
        element.get('subject').clearValidators();
      });
    }
    else{
      this.t.controls.forEach(element => {
        element.get('subject').setValidators(Validators.required);
      });
    }
    this.editTemplateForm.updateValueAndValidity();
    this.cpf.template.updateValueAndValidity();
    if (this.templateInfo && this.editTemplateForm.valid && this.cpf.template.valid) {
      isDisabled = false;
    }
    return isDisabled;
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  editBody(event) {
    this.templateInfo.body = event;
  }

  OnClose(isRefresh: boolean = false, isCopy: boolean = false) {
    this.activeModal.close({ success: true, refresh: isRefresh, isCopy: isCopy, templateInfo: this.templateInfo });
  }

  //#endregion

  //#region API Methods

  GetTemplateDetails(templateId: number) {
    this.spinner.show();
    this.editTemplateService.GetTemplate(templateId).then(result => {
      this.templateInfo = result.responseObject;
      this.setLists();
      this.getLanguageForm();
      this.onModuleChange();
      this.editTemplateForm.patchValue({
        templateScope: this.templateInfo.templateScope
      });
    }).finally(() => {
      this.spinner.hide();
    });
  }

   SaveTemplateDetails() {
    this.spinner.show();
    let successMessage = this.translate.instant("lblTemplateDetailsAreEdited");
    let existsErrorMessage = this.translate.instant("lblTemplateAlreadyExists");
    let errorMessage = this.translate.instant("lblErrorMessage");
    
    this.quillEditors.forEach((quill, index) => {
      this.templateDescriptions[index].body = quill.root.innerHTML;
    });
    this.templateInfo.templateDescriptions = this.templateDescriptions;
    this.templateInfo.templateScope = this.editTemplateForm.get('templateScope')?.value;

    this.editTemplateService.UpdateTemplate(this.templateInfo).then(res => {
      if (res.responseObject.errorCount == 0) {
        if (res.responseObject.result > 0) {
          this.toaster.success(successMessage);
          this.OnClose(true);
        }
        else{
          this.toaster.error(existsErrorMessage);
        } 
      }
      else{
        this.toaster.error(errorMessage);
      }
    }).finally(() => {
      this.spinner.hide();
    });
  }
  OpenTemplateFieldHelp(){
   //this is intentional
  }

  //#endregion
}
